import React, { useState, useMemo } from 'react'
import omit from 'lodash/omit'
import {
   Typography,
   Button,
   FormControl,
   InputLabel,
   Select,
   OutlinedInput,
   FormHelperText,
   MenuItem,
   TextField
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import TooltipInputError from '../../toolTip/ToolTipInputError'
import { Autocomplete } from '@material-ui/lab'
import Utils from 'utils/utils'
import TRANSLATE, { VALUES, OPERACIONES_FUNCIONALIDAD } from 'utils/constants'
import { useHasOperation } from 'hooks/useHasOperation'

const useStyles = makeStyles(theme => ({
   formContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '95%',
      margin: '0 auto 10px'
   },
   formControl: {
      width: '100%',
      minWidth: 120,
      textAlign: 'initial',
      fontSize: 14,
      '&:not(:first-child)': {
         marginTop: 20
      }
   },
   formControlTextArea: {
      width: '100%',
      minWidth: 120,
      textAlign: 'initial',
      fontSize: '14px',
      '&:not(:first-child)': {
         marginTop: 20
      }
   },
   button: {
      width: '100%',
      height: 40,
      marginTop: 20,
      color: 'white',
      background: '#3264DC'
   },
   formCleanButton: {
      color: '#3264DC',
      border: '1px solid #3264DC',
      fontWeight: 'bold',
      marginTop: 20,
      width: '100%'
   },
   helperText: {
      marginLeft: 0,
      marginRight: 0,
      marginTop: 0,
      fontSize: 11
   },
   selectEmpty: {
      marginTop: theme.spacing(2)
   },
   disabledButton: {
      backgroundColor: '#12B3F9!important',
      opacity: '0.3',
      color: '#fff!important'
   },
   MuiFormHelperText: {
      root: {
         margin: '0 !important'
      },
      margin: '0 !important'
   },
   chips: {
      display: 'flex',
      flexWrap: 'wrap'
   },
   chip: {
      margin: 2
   },
   multipleSelectLabel: {
      backgroundColor: 'white'
   },
   titleOne: {
      margin: '20px 0 15px 9px',
      fontStyle: 'normal',
      fontSize: 14,
      alignSelf: 'start',
      color: '#8A8FAB',
      fontWeight: 400
   }
}))

const NO_MORE_THAN_150_CHARACTERS = Utils.noMoreThanNCharacters(150)
const NO_MORE_THAN_255_CHARACTERS = Utils.noMoreThanNCharacters(255)

const INITIAL_FUNCTIONALITY_FORM_VALIDATION = {
   name: false,
   status: false,
   description: false,
   module: false,
   link: false,
   application: false
}

const validRequired = value => !value || value === ''

const validationsFields = {
   name: value => value.length > 150 || validRequired(value),
   // value.length > 150 || !REGEXP.VALIDATION_NAME.exec(value) || validRequired(value),
   description: value => value.length > 255 || validRequired(value),
   link: validRequired,
   module: validRequired,
   status: validRequired,
   application: validRequired
}

const FunctionalityForm = ({
   formData,
   setForm,
   modules,
   setModules,
   actionSubmit,
   formBehavior,
   cleanCallback,
   applications,
   getModulesByApplicationId
}) => {
   const classes = useStyles()

   const CONSULTAR_FUNCIONALIDAD = useHasOperation(OPERACIONES_FUNCIONALIDAD.CONSULTAR)
   const REGISTRAR_FUNCIONALIDAD = useHasOperation(OPERACIONES_FUNCIONALIDAD.REGISTRAR)

   const isSearch = useMemo(
      () => formBehavior === VALUES.FORM_BEHAVIOR.SEARCH,
      [formBehavior]
   )

   const [invalid, setInvalid] = useState(INITIAL_FUNCTIONALITY_FORM_VALIDATION)

   const cleanForm = () => {
      cleanCallback()
      setInvalid(INITIAL_FUNCTIONALITY_FORM_VALIDATION)
   }

   function isApplicationEmpty() {
      return validRequired(formData.application)
   }

   const validateFields = (name, value) => {
      const isInvalidFn =
         name in validationsFields ? validationsFields[name] : () => {}
      const isInvalid = isInvalidFn(value)
      if (isInvalid) {
         setInvalid(inv => ({
            ...inv,
            [name]: true
         }))
      } else if (invalid[name]) {
         setInvalid(inv => ({
            ...inv,
            [name]: false
         }))
      }
   }

   const addValuesToMemory = e => {
      const { name, value } = e.target
      if (!isSearch) {
         validateFields(name, value)
      } else if (isSearch && name === 'module') {
         validateFields('module', value)
      }
      setForm(prevFormData => ({
         ...prevFormData,
         [name]: value
      }))
   }

   const registerFunctionalities = type => {
      let isInvalidField = false
      const formFields =
         type === VALUES.FORM_BEHAVIOR.REGISTER
            ? omit(INITIAL_FUNCTIONALITY_FORM_VALIDATION, 'status')
            : INITIAL_FUNCTIONALITY_FORM_VALIDATION
      const newValidations = {}
      for (const key in formFields) {
         const isInvalid = validationsFields[key](formData[key])
         newValidations[key] = isInvalid
         if (isInvalid && !isInvalidField) {
            isInvalidField = isInvalid
         }
      }
      if (isInvalidField) {
         setInvalid(prev => {
            return { ...prev, ...newValidations }
         })
      } else {
         actionSubmit(type)
      }
   }

   const actionSubmitEvent = e => {
      e.preventDefault()
      switch (formBehavior) {
         case VALUES.FORM_BEHAVIOR.REGISTER: {
            registerFunctionalities(VALUES.FORM_BEHAVIOR.REGISTER)
            break
         }
         case VALUES.FORM_BEHAVIOR.SEARCH: {
            if (formData.application !== '' && formData.module === '') {
               setInvalid(prev => {
                  return { ...prev, module: true }
               })
               break
            }
            actionSubmit(VALUES.FORM_BEHAVIOR.SEARCH)
            break
         }
         case VALUES.FORM_BEHAVIOR.EDIT: {
            registerFunctionalities(VALUES.FORM_BEHAVIOR.EDIT)
            break
         }
         default:
            break
      }
   }

   function cleanModule() {
      addValuesToMemory({
         target: {
            name: 'module',
            value: ''
         }
      })
      setInvalid(prev => {
         return { ...prev, module: false }
      })
      setModules([])
   }

   function onApplicationChange(e, newValue) {
      addValuesToMemory({
         target: {
            name: 'application',
            value: newValue
         }
      })
      if (newValue && newValue.id) {
         getModulesByApplicationId(newValue.id)
      } else {
         cleanModule()
      }
   }

   return (
      <div className={classes.formContainer}>
         <Typography className={classes.titleOne}>
            <b> Completa los campos </b>{' '}
         </Typography>

         <form className={classes.formContainer}>
            <FormControl
               variant="outlined"
               size="small"
               className={classes.formControl}
               error={invalid.name}
            >
               <InputLabel htmlFor="outlined-adornment-amount">
                  {`Nombre de la Funcionalidad ${!isSearch ? '*' : ''}`}
               </InputLabel>
               <OutlinedInput
                  error={invalid.name}
                  value={formData.name}
                  onChange={e => {
                     const value = e.target.value.toUpperCase()
                     addValuesToMemory({
                        target: { name: 'name', value }
                     })
                     addValuesToMemory({
                        target: {
                           name: 'link',
                           value: Utils.validatePath(
                              Utils.buildUrlFromText(value)
                           )
                        }
                     })
                  }}
                  name="name"
                  size="small"
                  label="Nombre de la Funcionalidad "
                  inputProps={{ maxLength: 150 }}
                  endAdornment={
                     (invalid.name && (
                        <TooltipInputError title={'Ingrese un nombre valido'} />
                     )) ||
                     (formData.name.length === 150 && (
                        <TooltipInputError
                           title={NO_MORE_THAN_150_CHARACTERS}
                        />
                     ))
                  }
               />
               <FormHelperText className={classes.helperText}>
                  {invalid.name &&
                     'El campo nombre de la Funcionalidad es obligatorio'}
               </FormHelperText>
            </FormControl>

            {!isSearch && (
               <FormControl
                  variant="outlined"
                  size="small"
                  className={classes.formControlTextArea}
                  error={invalid.description}
               >
                  <InputLabel htmlFor="outlined-adornment-amount">
                     {`Descripción de la Funcionalidad ${!isSearch ? '*' : ''}`}
                  </InputLabel>
                  <OutlinedInput
                     error={invalid.description}
                     value={formData.description}
                     onChange={addValuesToMemory}
                     label="Descripción de la Funcionalidad  "
                     name="description"
                     endAdornment={
                        (invalid?.description && (
                           <TooltipInputError
                              title={'Ingrese una descripcion valida'}
                           />
                        )) ||
                        (formData?.description?.length === 255 && (
                           <TooltipInputError
                              title={NO_MORE_THAN_255_CHARACTERS}
                           />
                        ))
                     }
                     multiline
                     rows={4}
                     inputProps={{ maxLength: 255 }}
                  />
                  <FormHelperText className={classes.helperText}>
                     {invalid.description &&
                        'El campo Descripción de la Funcionalidad es obligatorio'}
                  </FormHelperText>
               </FormControl>
            )}

            {formBehavior !== VALUES.FORM_BEHAVIOR.REGISTER && (
               <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  size="small"
                  error={invalid.status}
               >
                  <InputLabel htmlFor="outlined-type-native-simple">
                     {`Estado de la Funcionalidad ${!isSearch ? '*' : ''}`}
                  </InputLabel>
                  <Select
                     value={formData.status}
                     onChange={addValuesToMemory}
                     size="small"
                     name="status"
                     label="Estado de la Funcionalidad  "
                     inputProps={{
                        name: 'status',
                        id: 'outlined-type-native-simple'
                     }}
                  >
                     <MenuItem aria-label="None" value={''} />
                     {VALUES.STATUS_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                           {option.label}
                        </MenuItem>
                     ))}
                  </Select>
                  <FormHelperText className={classes.helperText}>
                     {invalid.status &&
                        'El campo Estado de la Funcionalidad es obligatorio'}
                  </FormHelperText>
               </FormControl>
            )}

            {!isSearch && (
               <FormControl
                  variant="outlined"
                  size="small"
                  className={classes.formControl}
                  error={invalid.link}
               >
                  <InputLabel htmlFor="outlined-adornment-amount">
                     {`Url de la Funcionalidad ${!isSearch ? '*' : ''}`}
                  </InputLabel>
                  <OutlinedInput
                     error={invalid.link}
                     value={formData.link}
                     onChange={e => {
                        addValuesToMemory({
                           target: {
                              name: 'link',
                              value: Utils.validatePath(
                                 Utils.buildUrlFromText(e.target.value)
                              )
                           }
                        })
                     }}
                     name="link"
                     size="small"
                     label="Url de la Funcionalidad "
                     endAdornment={
                        invalid.link && (
                           <TooltipInputError
                              title={'ingrese una url válida'}
                           />
                        )
                     }
                  />
                  <FormHelperText className={classes.helperText}>
                     {invalid.link &&
                        'El campo Url de la Funcionalidad es obligatorio'}
                  </FormHelperText>
               </FormControl>
            )}

            <FormControl
               size="small"
               variant="outlined"
               className={classes.formControl}
               error={invalid.application}
            >
               <Autocomplete
                  size="small"
                  options={applications}
                  getOptionLabel={option => option.name}
                  renderOption={option => (
                     <span key={option.id}>{option.name}</span>
                  )}
                  value={formData.application}
                  onChange={onApplicationChange}
                  renderInput={params => (
                     <TextField
                        {...params}
                        label={`Nombre de Aplicación ${!isSearch ? '*' : ''}`}
                        variant="outlined"
                        value={''}
                        error={invalid.application}
                     />
                  )}
               />
               <FormHelperText className={classes.helperText}>
                  {invalid.application &&
                     'El campo Nombre de la Aplicación es obligatorio'}
               </FormHelperText>
            </FormControl>

            <FormControl
               size="small"
               variant="outlined"
               className={classes.formControl}
               error={invalid.module}
            >
               <Autocomplete
                  size="small"
                  options={modules}
                  getOptionLabel={option => option.name}
                  renderOption={option => (
                     <span key={option.id}>{option.name}</span>
                  )}
                  value={formData.module}
                  onChange={(e, newValue) => {
                     addValuesToMemory({
                        target: {
                           name: 'module',
                           value: newValue
                        }
                     })
                  }}
                  renderInput={params => (
                     <TextField
                        {...params}
                        label={`Nombre del Módulo ${!isSearch ? '*' : ''}`}
                        variant="outlined"
                        value={''}
                        error={invalid.module}
                     />
                  )}
                  disabled={isApplicationEmpty()}
               />
               <FormHelperText className={classes.helperText}>
                  {invalid.module &&
                     'El campo Nombre del Módulo es obligatorio'}
               </FormHelperText>
            </FormControl>

            <Button
               type="submit"
               fullWidth
               variant="contained"
               color="primary"
               className={classes.button}
               onClick={actionSubmitEvent}
               disabled={formBehavior === VALUES.FORM_BEHAVIOR.SEARCH 
                  ? !CONSULTAR_FUNCIONALIDAD : !REGISTRAR_FUNCIONALIDAD
               }
            >
               {formBehavior === VALUES.FORM_BEHAVIOR.EDIT
                  ? TRANSLATE.BUTTONS.EDIT
                  : isSearch
                  ? TRANSLATE.BUTTONS.SEARCH
                  : TRANSLATE.BUTTONS.SAVE}
            </Button>

            <Button
               classes={{
                  root: classes.formCleanButton
               }}
               variant="outlined"
               onClick={cleanForm}
            >
               {TRANSLATE.BUTTONS.CLEAN}
            </Button>
         </form>
      </div>
   )
}

export default FunctionalityForm
